import * as React from 'react'

import { Site } from '@envato/foundation-types'

import { config } from '../../../../common/config'
import { hasFeature } from '../../../../common/utils/features'
import NextGenImageWrapper from '../../nextGenImageWrapper'
import SeoHeadings from '../../seoHeadings/seoHeadings'
import { ICommonLocation } from '../../../../common/utils/location/types'
import { IExperimentsState } from '../../../../common/reducers/experiments'

import * as styles from './searchHeaderBlock.module.css'
import * as seoHeadingsStyles from '../../../components/seoHeadings/themes/homepage.newdesign.module.css'
import * as seoHeadingsSmallStyles from '../../../components/seoHeadings/themes/homepage.newdesign.small.module.css'
import * as seoHeadingsSmallCenteredStyles from '../../../components/seoHeadings/themes/homepage.newdesign.small.centred.module.css'
import * as classnames from 'classnames'

interface IProps {
  originalHeader?: string
  originalSubHeading?: string
  currentSite: Site
  location: ICommonLocation
  experiments: IExperimentsState
}

const siteHeaderTitleMap = new Map([
  [Site.themeforest, 'Thousands of WordPress themes and website templates'],
  [Site.codecanyon, 'Discover thousands of scripts, plugins and code'],
  [Site.videohive, 'Discover millions of video effects and stock footage'],
  [
    Site.audiojungle,
    'Discover thousands of royalty free music and audio tracks'
  ],
  [Site.graphicriver, 'Discover thousands of fonts, logos & presentations'],
  [Site.photodune, 'Discover millions of royalty-free stock photos'],
  [Site.threedocean, 'Discover thousands of 3D print models and CG textures']
])

const siteHeaderTitleMapVariantWithoutDollar = new Map([
  [Site.themeforest, '{itemCount} WordPress Themes & Website Templates'],
  [Site.codecanyon, 'Discover {itemCount} scripts, plugins and code'],
  [Site.videohive, 'Discover {itemCount} Video effects and stock footage'],
  [Site.audiojungle, 'Royalty free music and audio tracks'],
  [Site.graphicriver, 'Discover {itemCount} fonts, logos & presentations'],
  [Site.photodune, 'Discover royalty free stock photos'],
  [Site.threedocean, 'Discover {itemCount} 3D print models and CG textures']
])

const siteHeaderTitleMapVariantWithDollar = new Map([
  [
    Site.themeforest,
    '{itemCount} WordPress themes and website templates from $2'
  ],
  [Site.codecanyon, 'Discover {itemCount} scripts, plugins, and code from $2'],
  [
    Site.videohive,
    'Discover {itemCount} Video effects and stock footage from $4'
  ],
  [
    Site.audiojungle,
    'Discover {itemCount} royalty free music and audio tracks from $1'
  ],
  [
    Site.graphicriver,
    'Discover {itemCount} fonts, logos & presentations from $2'
  ],
  [Site.photodune, 'Discover royalty free stock photos from $2'],
  [
    Site.threedocean,
    'Discover {itemCount} 3D print models and CG textures from $2'
  ]
])

const getDesktopImageUrl = (
  currentSite: string,
  hasNewHomeHeaderImages: boolean
) => {
  const filename = hasNewHomeHeaderImages
    ? 'search-header-new'
    : 'search-header'
  return `${config.assets.PUBLIC_PATH}/images/home-page/new-design/${currentSite}/${filename}-1600749088571.webp`
}

const SearchHeaderBlock: React.SFC<IProps> = ({
  children,
  currentSite,
  originalHeader,
  originalSubHeading,
  location,
  experiments
}) => {
  const hasOgCopyVerbatim = hasFeature(
    'ogCopyVerbatim',
    currentSite,
    location,
    experiments
  )

  const hasCombinedCopyNoDollars = hasFeature(
    'combinedCopyNoDollars',
    currentSite,
    location,
    experiments
  )

  const hasCombinedCopyWithDollars = hasFeature(
    'combinedCopyWithDollars',
    currentSite,
    location,
    experiments
  )

  const hasNewHomeHeaderImages = hasFeature(
    'newHomeHeaderImages',
    currentSite,
    location,
    experiments
  )

  const hasHomeHeaderCopyOnly = hasFeature(
    'homeHeaderCopyOnly',
    currentSite,
    location,
    experiments
  )

  const imageStyle =
    currentSite === Site.audiojungle && !hasNewHomeHeaderImages
      ? styles.imageWrapperAudio
      : styles.imageWrapper

  let header = siteHeaderTitleMap.get(currentSite)
  let subHeader
  let headerStyle = seoHeadingsStyles
  let seoHeaderClass = styles.seoHeader

  if (hasOgCopyVerbatim) {
    header = originalHeader
    subHeader = originalSubHeading
    headerStyle = seoHeadingsSmallStyles
    seoHeaderClass = styles.seoHeaderSmall
  }

  if (hasHomeHeaderCopyOnly) {
    headerStyle = seoHeadingsSmallCenteredStyles
  }

  if (hasCombinedCopyNoDollars || hasCombinedCopyWithDollars) {
    const itemCount =
      originalHeader?.match(/\s?([0-9,])+ /) ||
      originalSubHeading?.match(/\s?([0-9,])+ /)

    header = (hasCombinedCopyNoDollars
      ? siteHeaderTitleMapVariantWithoutDollar
      : siteHeaderTitleMapVariantWithDollar
    ).get(currentSite)

    if (itemCount) {
      header = header?.replace('{itemCount}', itemCount[0])
    }
  }

  return (
    <section className={styles.root}>
      <div
        className={classnames(styles.wrapper, {
          [styles.wrapperImageless]: hasHomeHeaderCopyOnly
        })}
      >
        <div className={styles.autosuggestWrapper}>
          <div className={seoHeaderClass}>
            <SeoHeadings
              TagName="h1"
              heading={
                header || 'Discover thousands of web themes and templates'
              }
              subHeading={subHeader || ''}
              styles={headerStyle}
            />
          </div>
          <div className={styles.childrenContainer}>{children}</div>
        </div>
        {!hasHomeHeaderCopyOnly && (
          <div className={imageStyle}>
            <NextGenImageWrapper>
              <img
                src={getDesktopImageUrl(currentSite, hasNewHomeHeaderImages)}
                alt={`${currentSite} hero image`}
                className={styles.image}
              />
            </NextGenImageWrapper>
          </div>
        )}
      </div>
    </section>
  )
}

export default SearchHeaderBlock
